import dayjs from 'dayjs';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { PropTypes } from 'prop-types';
import React, { useCallback, useContext, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Loader } from '@ge/components/loader';
import { Select } from '@ge/components/select';
import { SortDirection } from '@ge/components/table/models/sort-direction';
import { useColumnState } from '@ge/components/table/use-column-state';
import { useManageColumnVisibility } from '@ge/components/table/use-manage-column-visibility';
import { Text } from '@ge/components/typography';
import { DateFilter } from '@ge/feat-inspections/components/date-filter';
import { EventRefreshButtonWithToolTip } from '@ge/feat-monitor/components/event-refresh';
import { useFeaturePrefs } from '@ge/hooks/feature-prefs';
import { MonitorDefs, ManageDefs, TableType, Capability,  PermissionScope } from '@ge/models/constants';
import { AuthRender } from '@ge/shared/components/auth-render';
import { ColumnSelectMenu } from '@ge/shared/components/column-select-menu';
import AlertHistoryTable from '@ge/shared/components/tables/alert-history-table';
import AnomalyTaskTable from '@ge/shared/components/tables/anomaly-task-table';
import CaseHistoryTable from '@ge/shared/components/tables/case-history-table';
import TurbineEventsHistoryTable from '@ge/shared/components/tables/turbine-events-history-table';
import { EntityDetailsContext } from '@ge/shared/context/entity-details-context';
import {
  useAssetCaseHistory,
  useAssetTaskHistory,
  useTurbineTaskHistory,
  useAnomalyCaseHistory,
} from '@ge/shared/data-hooks';
import { useAssetAlertHistory } from '@ge/shared/data-hooks/use-asset-alert-history';
import { AppScopes } from '@ge/shared/models/scopes';
import {
  AlertHistoryColumnDefs,
  defaultAlertHistoryCols,
} from '@ge/shared/models/table-col-defs/alert-history-table-cols';
import {
  defaultAnomalyTasksCols,
  AnomalyTaskColumnDefs,
} from '@ge/shared/models/table-col-defs/anomaly-task-table-cols';
import {
  CaseHistoryColumnDefs,
  defaultCaseHistoryCols,
} from '@ge/shared/models/table-col-defs/case-history-table-cols';
import {
  defaultTurbineHistoryCols,
  TurbineHistoryColumnDefs,
} from '@ge/shared/models/table-col-defs/turbine-history-table-cols';
import { killEventPropagation, updatedTaskHistoryData } from '@ge/shared/util/general';
import { typography } from '@ge/tokens/typography';

import { TabDetailContainer } from '../../entity-details-shared';

const EventMetric = {
  START: 'start',
};

//Turbine
const TurbineMetric = {
  START: 'startDate',
};

const AlertMetric = {
  CREATED_DATE: 'createdDate',
};

export const TaskMetric = {
  COMPLETED_DATE_TIME: 'completedDateTime',
};

const AnomalyMetric = {
  START_DATE: 'startDate',
};
export const OverviewTitle = styled.div`
  align-items: center;
  display: flex;
  flex-flow: row nowrap;
  .history {
    line-height: 21px;
    margin: 30px 20px 35px 30px;
  }
  .turbineEvents {
    z-index: 99;
    width: 368px;
    padding: 0;
    margin: 0;
  }
`;
const Banner = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin: 110px 0;
  align-item: center;
  justify-content: center;
  text-align: center;
  span {
    padding-top: 10px;
  }
`;
const NoDataTitle = styled(Text).attrs(() => ({
  type: typography.textTypes.body,
  level: 1,
}))`
  margin-bottom: 6px;
`;
const Load = styled.div`
  padding-top: 400px;
`;
const TableActionsContainer = styled.div`
  display:flex
  flex-direction:row;
  justify-content:flex-end;
  align-items:center;
`;
const TurbineTableContainer = styled.div`
  margin-right: 10px;
`;

const AssetTaskHistory = styled.div`
  min-height: 250px;
  height: 100%;
`;

//Turbine details

export const AssetHistory = ({
  assetId,
  isAuthorizedToViewTasks,
  isAuthorizedToMonitorCases,
  isAuthorizedAlertsHistory,
}) => {
  const { t, ready } = useTranslation(['manage.cases-tasks', 'monitor.issues'], {
    useSuspense: false,
  });

  const HistorySelectOptions = [
    { label: t('selectMenu.last7_days', 'Last 7 Days'), value: 7 },
    { label: t('selectMenu.last30_days', 'Last 30 Days'), value: 30 },
    { label: t('selectMenu.last90_days', 'Last 90 Days'), value: 90 },
    { label: t('selectMenu.last6_months', 'Last 6 Months'), value: 180 },
    { label: t('selectMenu.last1_year', 'Last 1 Year'), value: 365 },
  ];

  const AlertHistorySelectOptions = [
    { label: t('selectMenu.last30_days', 'Last 30 Days'), value: 30 },
    { label: t('selectMenu.last90_days', 'Last 90 Days'), value: 90 },
  ];

  //if any other capibilities is added in future then respective dropdown values can be added inside options
  const SelectOptions = useMemo(() => {
    const options = [
      {
        value: TableType.TASK_HISTORY,
        label: t('selectMenu.task_completion', 'Asset History Task Completion History'),
        enabled: isAuthorizedToViewTasks,
      },
      {
        value: TableType.EVENT,
        label: t('selectMenu.case_history', 'Asset History - Case'),
        enabled: isAuthorizedToMonitorCases,
      },
      {
        value: TableType.ALERT_HISTORY,
        label: t('selectMenu.alert_history', 'Asset History - Alert'),
        enabled: isAuthorizedAlertsHistory,
      },
      {
        value: TableType.TURBINE_HISTORY,
        label: t('selectMenu.turbine_history', 'Asset History - Turbine Event List'),
        enabled: isAuthorizedToViewTasks,
      },
      {
        value: TableType.ANOMALY_CASE_HISTORY,
        label: t('selectMenu.anomaly_cases', 'Asset History - Anomaly Cases History'),
        enabled: isAuthorizedToViewTasks,
      },
    ];
    return options.filter((op) => op.enabled);
  }, [isAuthorizedToMonitorCases, isAuthorizedToViewTasks, isAuthorizedAlertsHistory, t]);
  const [timeRange, setTimeRange] = useState(HistorySelectOptions[2]);
  const [alertTimeRange, setAlertTimeRange] = useState(AlertHistorySelectOptions[0]);
  const [tableType, setTableType] = useState(SelectOptions[0]?.value);
  const [filterDefs, setFilterDefs] = useState({});
  const [currentSearch, setCurrentSearch] = useState({});

  const filterDate = useStoreState((state) => state.assets.turbineEventsDateRange);
  const filterAnamolyDate = useStoreState((state) => state.assets.anomalyCaseHistoryDateRange);
  const { updateAnamolyDateFilter } = useStoreActions((actions) => actions.assets);

  const { updateDateFilter } = useStoreActions((actions) => actions.assets);
  const { getAssetById } = useStoreState((store) => store.assets);
  const { getSiteById } = useStoreState((store) => store.sites);
  const asset = getAssetById(assetId);
  const site = getSiteById(asset?.site?.id);
  const timezone = site?.timezone ?? 'UTC';

  // Table state management
  const {
    sortDirection,
    sortMetric,
    updateSortMetric: toggleHistoryTaskSort,
    visibleCols: visibleAssetHistoryCols,
    updateColumnVisibility: updateAssetHistoryColumnVisibility,
    setVisibleColumns: setVisibleAssetHistoryCols,
  } = useColumnState({
    columnDefs: CaseHistoryColumnDefs,
    defaultCols: defaultCaseHistoryCols,
    defaultSortMetric: TaskMetric.COMPLETED_DATE_TIME,
    defaultSortDirection: SortDirection.DESC,
    sortStateId: ManageDefs.HISTORY,
  });

  //Turbine History Details
  const {
    sortDirection: sortTurbineHistoryDirection,
    sortMetric: sortTurbineHistoryMetric,
    updateSortMetric: toggleTurbineHistorySort,
    visibleCols: visibleTurbineHistoryCols,
  } = useColumnState({
    columnDefs: TurbineHistoryColumnDefs,
    defaultCols: defaultTurbineHistoryCols,
    defaultSortMetric: TurbineMetric.START,
    defaultSortDirection: SortDirection.DESC,
  });
  //Anomaly task Details
  const {
    sortDirection: sortAnomalyTaskDirection,
    sortMetric: sortAnomalyMetric,
    updateSortMetric: toggleAnomalyTaskSort,
    visibleCols: visibleAnomalyTaskCols,
  } = useColumnState({
    columnDefs: AnomalyTaskColumnDefs,
    defaultCols: defaultAnomalyTasksCols,
    defaultSortMetric: AnomalyMetric.START_DATE,
    defaultSortDirection: SortDirection.DESC,
  });

  const { showTaskDetails } = useContext(EntityDetailsContext);

  const handleSelect = useCallback((option) => setTableType(option.value), []);

  const selectedHistoryType = useMemo(
    () => SelectOptions.filter((option) => option.value === tableType),
    [SelectOptions, tableType],
  );
  const {
    sortDirection: sortHistoryDirection,
    sortMetric: sortHistoryMetric,
    updateSortMetric: toggleHistorySort,
  } = useColumnState({
    columnDefs: CaseHistoryColumnDefs,
    defaultCols: defaultCaseHistoryCols,
    defaultSortMetric: EventMetric.START,
    defaultSortDirection: SortDirection.DESC,
    sortStateId: MonitorDefs.HISTORY,
  });

  const {
    sortDirection: sortAlertHistoryDirection,
    sortMetric: sortAlertHistoryMetric,
    updateSortMetric: toggleAlertHistorySort,
    visibleCols: visibleAlertHistoryCols,
  } = useColumnState({
    columnDefs: AlertHistoryColumnDefs,
    defaultCols: defaultAlertHistoryCols,
    defaultSortMetric: AlertMetric.CREATED_DATE,
    defaultSortDirection: SortDirection.DESC,
    sortStateId: MonitorDefs.HISTORY,
  });

  const filteredColumns = useMemo(() => {
    const filteredColDefs = defaultCaseHistoryCols?.reduce((prevDefs, c) => {
      const cols = c?.cols?.reduce((prevCols, col) => {
        if (col?.filter === undefined || col?.filter === tableType) {
          prevCols[col.id] = CaseHistoryColumnDefs[c.id]?.cols?.[col.id];
        }
        return prevCols;
      }, {});
      if (Object.keys(cols).length) {
        prevDefs[c.id] = { cols };
      }
      return prevDefs;
    }, {});
    return filteredColDefs;
  }, [tableType]);

  const {
    data: caseHistoryData,
    isLoading: isCaseHistoryDataLoading,
    refetch: refetchCaseHistoryData,
    isRefetching: isCaseHistoryDataRefetching,
  } = useAssetCaseHistory({
    assetId,
    days: timeRange.value,
    sortDirection: sortHistoryDirection,
    sortMetric: sortHistoryMetric,
    enabled: tableType === TableType.EVENT && isAuthorizedToMonitorCases,
  });
  const { taskUpdateForAsset } = useStoreState((state) => state.tasks);
  const {
    data: taskHistoryData,
    isLoading: isTaskHisotryDataLoading,
    filterValues,
  } = useAssetTaskHistory({
    filters: filterDefs,
    search: currentSearch,
    assetId,
    sortDirection,
    sortMetric,
    enabled: tableType === TableType.TASK_HISTORY,
    taskUpdateForAsset: taskUpdateForAsset[assetId] ?? assetId,
  });

  const {
    data: alertHistoryData,
    isLoading: isAlertHistoryDataLoading,
    refetch: refetchAlertHistoryData,
    isRefetching: isAlertHistoryDataRefetching,
    filterValues: alertFilterValues,
  } = useAssetAlertHistory({
    filters: filterDefs,
    search: currentSearch,
    assetId,
    days: alertTimeRange.value,
    sortDirection: sortAlertHistoryDirection,
    sortMetric: sortAlertHistoryMetric,
    enabled: tableType === TableType.ALERT_HISTORY,
  });

  //TurbineHistory
  const {
    data: turbineHistoryData,
    filterValues: turbineHistoryFilterValues,
    isLoading: isTurbineHistoryDataLoading,
  } = useTurbineTaskHistory({
    filters: filterDefs,
    search: currentSearch,
    assetId,
    sortMetric: sortTurbineHistoryMetric,
    sortDirection: sortTurbineHistoryDirection,
    enabled: tableType === TableType.TURBINE_HISTORY,
    startDate: dayjs.tz(filterDate.startDate, timezone).valueOf() / 1000,
    endDate: dayjs.tz(filterDate.endDate, timezone).valueOf() / 1000,
  });

  const {
    data: AnomalyTaskData,
    filterValues: anomalyFilterValues,
    isLoading: isAnomalyTaskDataLoading,
  } = useAnomalyCaseHistory({
    filters: filterDefs,
    search: currentSearch,
    assetId,
    sortMetric: sortAnomalyMetric,
    sortDirection: sortAnomalyTaskDirection,
    enabled: tableType === TableType.ANOMALY_CASE_HISTORY,
    startDate: dayjs.tz(filterAnamolyDate.startDate, timezone).valueOf() / 1000,
    endDate: dayjs.tz(filterAnamolyDate.endDate, timezone).valueOf() / 1000,
  });

  const { savePrefs, featPrefs } = useFeaturePrefs(AppScopes.MONITOR_ASSETS);

  const { onVisibilityChange: handleAssetHistoryColumnChanges } = useManageColumnVisibility({
    subKey: MonitorDefs.EVENTS,
    featPrefs,
    savePrefs,
    setVisibleColumns: setVisibleAssetHistoryCols,
    updateColumnVisibility: updateAssetHistoryColumnVisibility,
  });

  const historyTableProps = useMemo(() => {
    const dynamicProps = [
      {
        isLoading: isTaskHisotryDataLoading,
        history: updatedTaskHistoryData(taskHistoryData, timeRange),
        sortAction: toggleHistoryTaskSort,
        sortMetric,
        sortDirection,
        tableType: TableType.TASK_HISTORY,
      },
      {
        isLoading: isCaseHistoryDataLoading,
        history: caseHistoryData,
        sortAction: toggleHistorySort,
        sortMetric: sortHistoryMetric,
        sortDirection: sortHistoryDirection,
        tableType: TableType.EVENT,
      },
      {
        isLoading: isAlertHistoryDataLoading,
        history: alertHistoryData,
        sortAction: toggleAlertHistorySort,
        sortMetric: sortAlertHistoryMetric,
        sortDirection: sortAlertHistoryDirection,
        tableType: TableType.ALERT_HISTORY,
      },
      {
        isLoading: isTurbineHistoryDataLoading,
        history: turbineHistoryData,
        sortAction: toggleTurbineHistorySort,
        sortMetric: sortTurbineHistoryMetric,
        sortDirection: sortTurbineHistoryDirection,
        tableType: TableType.TURBINE_HISTORY,
      },
      {
        isLoading: isAnomalyTaskDataLoading,
        history: AnomalyTaskData,
        sortAction: toggleAnomalyTaskSort,
        sortMetric: sortAnomalyMetric,
        sortDirection: sortAnomalyTaskDirection,
        tableType: TableType.ANOMALY_CASE_HISTORY,
      },
    ];
    return dynamicProps.find((prop) => prop.tableType === tableType);
  }, [
    isTaskHisotryDataLoading,
    taskHistoryData,
    toggleHistoryTaskSort,
    sortMetric,
    sortDirection,
    isCaseHistoryDataLoading,
    caseHistoryData,
    toggleHistorySort,
    sortHistoryMetric,
    sortHistoryDirection,
    isAlertHistoryDataLoading,
    alertHistoryData,
    toggleAlertHistorySort,
    sortAlertHistoryMetric,
    sortAlertHistoryDirection,
    isTurbineHistoryDataLoading,
    turbineHistoryData,
    toggleTurbineHistorySort,
    sortTurbineHistoryMetric,
    sortTurbineHistoryDirection,
    tableType,
    isAnomalyTaskDataLoading,
    AnomalyTaskData,
    toggleAnomalyTaskSort,
    sortAnomalyMetric,
    sortAnomalyTaskDirection,
  ]);

  const { isLoading, history: data } = historyTableProps;
  const handleFilter = useCallback(
    (updateDefs) => {
      setFilterDefs(updateDefs);
    },
    [setFilterDefs],
  );

  const handleFilterChange = useCallback((search) => setCurrentSearch(search), [setCurrentSearch]);

  const handleEventSelect = useCallback((e) => {
    killEventPropagation(e);
  }, []);

  const handleAssetCaseHistoryRefresh = (e) => {
    killEventPropagation(e);
    refetchCaseHistoryData({ throwOnError: true });
  };

  const handleAssetAlertHistoryRefresh = (e) => {
    killEventPropagation(e);
    refetchAlertHistoryData({ throwOnError: true });
  };

  const handleDateFilterChange = useCallback(
    (payload) => {
      updateDateFilter(payload);
    },
    [updateDateFilter],
  );

  const handleAnamolyDateFilterChange = useCallback(
    (payload) => {
      updateAnamolyDateFilter(payload);
    },
    [updateAnamolyDateFilter],
  );

  const handleTaskSelect = useCallback(
    (_, task) => {
      showTaskDetails(task.id);
    },
    [showTaskDetails],
  );

  useEffect(() => {
    if (isAuthorizedToMonitorCases && tableType === TableType.EVENT) {
      setTimeRange(HistorySelectOptions[0]);
    }
  }, [tableType]);

  if (!ready || isLoading) {
    return (
      <Load>
        <Loader />
      </Load>
    );
  }
  return (
    <AssetTaskHistory>
      <TabDetailContainer>
        {!isLoading && (
          <OverviewTitle>
            {SelectOptions?.length && (
              <Select
                primary
                className="history"
                minWidth={360}
                maxWidth={360}
                options={SelectOptions}
                value={selectedHistoryType}
                onChange={handleSelect}
              />
            )}
            {tableType === TableType.EVENT || tableType === TableType.TASK_HISTORY ? (
              <Select
                small
                maxWidth={180}
                options={HistorySelectOptions}
                value={timeRange}
                onChange={(option) => setTimeRange(option)}
              />
            ) : null}

            {tableType === TableType.TURBINE_HISTORY && (
              <div className="turbineEvents">
                <DateFilter filterDate={filterDate} onChange={handleDateFilterChange} />
              </div>
            )}
            {tableType === TableType.ANOMALY_CASE_HISTORY && (
              <div className="turbineEvents">
                <DateFilter
                  filterDate={filterAnamolyDate}
                  onChange={handleAnamolyDateFilterChange}
                />
              </div>
            )}

            {tableType === TableType.ALERT_HISTORY ? (
              <Select
                small
                maxWidth={180}
                options={AlertHistorySelectOptions}
                value={alertTimeRange}
                onChange={(option) => setAlertTimeRange(option)}
              />
            ) : null}
          </OverviewTitle>
        )}
        {tableType === TableType.TASK_HISTORY ||
          (tableType === TableType.EVENT && (
            <AuthRender
              capability={Capability.CASES}
              view
              description="Column selector and event refresh"
              siteLevel={false}
            >
              <TableActionsContainer>
                <EventRefreshButtonWithToolTip
                  onClick={handleAssetCaseHistoryRefresh}
                  inProgress={isCaseHistoryDataRefetching}
                  toolTip={
                    isCaseHistoryDataRefetching
                      ? t('event_refresh.tooltip.in_progress', 'Refresh in Progress')
                      : t('event_refresh.tooltip.case_refresh', 'Case Refresh')
                  }
                />
                <ColumnSelectMenu
                  heightOffset={380}
                  translateFn={t}
                  columnDef={filteredColumns}
                  columnState={visibleAssetHistoryCols}
                  onMenuClose={handleAssetHistoryColumnChanges}
                />
              </TableActionsContainer>
            </AuthRender>
          ))}
        {tableType === TableType.ALERT_HISTORY && (
          <AuthRender
            capabilities={[{ capability: Capability.ALERTS, scopes: [PermissionScope.VIEW] } ]}
            description="Column selector and event refresh"
            siteLevel={false}
          >
            <TableActionsContainer>
              <EventRefreshButtonWithToolTip
                onClick={handleAssetAlertHistoryRefresh}
                inProgress={isAlertHistoryDataRefetching}
                toolTip={
                  isAlertHistoryDataRefetching
                    ? t('event_refresh.tooltip.in_progress', 'Refresh in Progress')
                    : t('event_refresh.tooltip.alert_refresh', 'Alert Refresh')
                }
              />
            </TableActionsContainer>
          </AuthRender>
        )}
        {(tableType === TableType.EVENT || tableType === TableType.TASK_HISTORY) && (
          <CaseHistoryTable
            columns={visibleAssetHistoryCols}
            tableType={tableType}
            onEventSelect={handleEventSelect}
            onTaskSelect={handleTaskSelect}
            onFilter={handleFilter}
            onFilterChange={handleFilterChange}
            filterValues={filterValues}
            {...historyTableProps}
          />
        )}
        {tableType === TableType.ALERT_HISTORY && (
          <AlertHistoryTable
            columns={visibleAlertHistoryCols}
            tableType={tableType}
            onEventSelect={handleEventSelect}
            onFilter={handleFilter}
            onFilterChange={handleFilterChange}
            filterValues={alertFilterValues}
            siteId={site?.id}
            {...historyTableProps}
          />
        )}
        {tableType === TableType.TURBINE_HISTORY && (
          <TurbineTableContainer>
            <TurbineEventsHistoryTable
              columns={visibleTurbineHistoryCols}
              tableType={tableType}
              onEventSelect={handleEventSelect}
              onFilter={handleFilter}
              onFilterChange={handleFilterChange}
              filterValues={turbineHistoryFilterValues}
              {...historyTableProps}
            />
          </TurbineTableContainer>
        )}
        {tableType === TableType.ANOMALY_CASE_HISTORY && (
          <AnomalyTaskTable
            columns={visibleAnomalyTaskCols}
            tableType={tableType}
            onEventSelect={handleEventSelect}
            onTaskSelect={handleTaskSelect}
            onFilter={handleFilter}
            onFilterChange={handleFilterChange}
            filterValues={anomalyFilterValues}
            {...historyTableProps}
          />
        )}
        {data.length === 0 && !isLoading && (
          <Banner>
            <span>
              {tableType === TableType.TASK_HISTORY && (
                <NoDataTitle>{t('no_tasks', 'No Tasks')}</NoDataTitle>
              )}
              {tableType === TableType.EVENT && (
                <NoDataTitle>{t('no_cases', 'No Cases')}</NoDataTitle>
              )}
              {tableType === TableType.ALERT_HISTORY && (
                <NoDataTitle>{t('no_alerts', 'No Alerts')}</NoDataTitle>
              )}
              {tableType === TableType.TURBINE_HISTORY && (
                <NoDataTitle>{t('no_turbine_history', 'No Turbine History Events')}</NoDataTitle>
              )}
              {tableType === TableType.ANOMALY_CASE_HISTORY && (
                <NoDataTitle>
                  {t('no__anomaly_case_history', 'No Anomaly Cases History')}
                </NoDataTitle>
              )}
            </span>
          </Banner>
        )}
      </TabDetailContainer>
    </AssetTaskHistory>
  );
};

AssetHistory.propTypes = {
  assetId: PropTypes.instanceOf(Object).isRequired,
  isAuthorizedToViewTasks: PropTypes.bool,
  isAuthorizedToMonitorCases: PropTypes.bool,
  isAuthorizedToAnalyzeCases: PropTypes.bool,
  isAuthorizedAlertsHistory: PropTypes.bool,
};
AssetHistory.defaultProps = {
  isAuthorizedToViewTasks: false,
  isAuthorizedToMonitorCases: false,
  isAuthorizedToAnalyzeCases: false,
  isAuthorizedAlertsHistory: false,
};
